import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  Output,
} from '@angular/core';

type ResizeUnit = 'px' | '%';
type ResizeEdge = 'left' | 'right';

@Directive({
  selector: '[irembogovRealtimeResize]',
})
export class RealtimeResizeDirective {
  @Input() irembogovRealtimeResizeValue = 0;
  @Input() irembogovRealtimeResizeMin = 0;
  @Input() irembogovRealtimeResizeMax = Number.POSITIVE_INFINITY;
  @Input() irembogovRealtimeResizeUnit: ResizeUnit = 'px';
  @Input() irembogovRealtimeResizeEdge: ResizeEdge = 'left';
  @Input() irembogovRealtimeResizeTargetSelector = '';
  @Input() irembogovRealtimeResizeContainerSelector = '';
  @Input() irembogovRealtimeResizeCompanionSelector = '';
  @Input() irembogovRealtimeResizeCompanionGapPx = 0;
  @Input() irembogovRealtimeResizeActiveClass = '';
  @Input() irembogovRealtimeResizeBodyClass = '';

  @Output() irembogovRealtimeResizeStarted = new EventEmitter<void>();
  @Output() irembogovRealtimeResizeCommitted = new EventEmitter<number>();

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone
  ) {}

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    event.preventDefault();
    const host = this.elementRef.nativeElement;
    const target = this.resolveElement(
      this.irembogovRealtimeResizeTargetSelector,
      host
    );
    if (!target) return;

    const container = this.resolveContainer(host, target);
    const companion = this.resolveElement(
      this.irembogovRealtimeResizeCompanionSelector,
      host,
      container
    );
    const containerWidth = container?.getBoundingClientRect().width || 0;
    const startX = event.clientX;
    const startValue = this.irembogovRealtimeResizeValue;
    let nextValue = startValue;

    this.addActiveClasses(container);
    this.irembogovRealtimeResizeStarted.emit();

    this.ngZone.runOutsideAngular(() => {
      const onMove = (moveEvent: PointerEvent) => {
        nextValue = this.nextValue(
          startValue,
          startX,
          moveEvent.clientX,
          containerWidth
        );
        this.applyWidth(target, companion, nextValue);
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        this.removeActiveClasses(container);
        this.ngZone.run(() => {
          this.irembogovRealtimeResizeCommitted.emit(nextValue);
        });
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    });
  }

  private nextValue(
    startValue: number,
    startX: number,
    clientX: number,
    containerWidth: number
  ): number {
    const direction = this.irembogovRealtimeResizeEdge === 'left' ? 1 : -1;
    const deltaPx = (startX - clientX) * direction;
    const delta =
      this.irembogovRealtimeResizeUnit === '%'
        ? containerWidth
          ? (deltaPx / containerWidth) * 100
          : 0
        : deltaPx;
    return this.clamp(startValue + delta);
  }

  private applyWidth(
    target: HTMLElement,
    companion: HTMLElement | null,
    value: number
  ): void {
    target.style.width = `${value}${this.irembogovRealtimeResizeUnit}`;
    if (
      companion &&
      this.irembogovRealtimeResizeUnit === '%' &&
      this.irembogovRealtimeResizeCompanionGapPx >= 0
    ) {
      companion.style.width = `calc(${100 - value}% - ${
        this.irembogovRealtimeResizeCompanionGapPx
      }px)`;
    }
  }

  private resolveContainer(
    host: HTMLElement,
    target: HTMLElement
  ): HTMLElement | null {
    if (!this.irembogovRealtimeResizeContainerSelector) {
      return target.parentElement;
    }
    return this.resolveElement(
      this.irembogovRealtimeResizeContainerSelector,
      host
    );
  }

  private resolveElement(
    selector: string,
    host: HTMLElement,
    root?: HTMLElement | null
  ): HTMLElement | null {
    if (!selector) return host;
    const closest = host.closest(selector);
    if (closest instanceof HTMLElement) return closest;
    const scope = root || host.parentElement || document.body;
    const found = scope.querySelector(selector);
    return found instanceof HTMLElement ? found : null;
  }

  private addActiveClasses(container: HTMLElement | null): void {
    if (this.irembogovRealtimeResizeActiveClass && container) {
      container.classList.add(this.irembogovRealtimeResizeActiveClass);
    }
    if (this.irembogovRealtimeResizeBodyClass) {
      document.body.classList.add(this.irembogovRealtimeResizeBodyClass);
    }
  }

  private removeActiveClasses(container: HTMLElement | null): void {
    if (this.irembogovRealtimeResizeActiveClass && container) {
      container.classList.remove(this.irembogovRealtimeResizeActiveClass);
    }
    if (this.irembogovRealtimeResizeBodyClass) {
      document.body.classList.remove(this.irembogovRealtimeResizeBodyClass);
    }
  }

  private clamp(value: number): number {
    return Math.min(
      this.irembogovRealtimeResizeMax,
      Math.max(this.irembogovRealtimeResizeMin, value)
    );
  }
}
